 
import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  LineChart, Line, AreaChart, Area, Sector,
} from 'recharts';
import { BarChart2, Users, Briefcase, FileText, User, Clock } from 'lucide-react';
import { fetchAttendanceCard } from '../../store/slices/attendanceSlice';
import EmployeePicker from '../../components/employee/EmployeePicker';
import MonthNavigator from '../../components/employee/MonthNavigator';
import AttendanceCardView from '../../components/employee/AttendanceCardView';

const IMAGE_BASE = 'https://esystems.cdl.lk/backendDGES/BizTrack/Home/DGESGetUserImg';

const fmt  = (v) => (!v || v === 'null' || v === 'undefined') ? '' : String(v).trim();
const fmtD = (v) => {
  if (!v || v === 'null') return '—';
  try { const d = new Date(v); return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB'); }
  catch { return String(v); }
};
function calcAge(dob) {
  if (!dob) return null;
  const b = new Date(dob); if (isNaN(b.getTime())) return null;
  const now = new Date(); let age = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age--;
  return age >= 0 ? age : null;
}
function calcYears(d) {
  if (!d) return null;
  const s = new Date(d); if (isNaN(s.getTime())) return null;
  const now = new Date(); let y = now.getFullYear() - s.getFullYear();
  if (now.getMonth() < s.getMonth() || (now.getMonth() === s.getMonth() && now.getDate() < s.getDate())) y--;
  return y >= 0 ? y : null;
}
const BLOOD_MAP = {
  '0':'O','1':'O+','2':'O-','3':'AB','4':'AB+',
  '5':'AB-','6':'A','7':'A+','8':'A-',
  '9':'B','10':'B+','11':'B-','12':'Unknown',
};

// ── Rich gold/amber/slate palette ────────────────────────────────────────────
const C = [
  '#f59e0b','#d97706','#b45309','#92400e',
  '#475569','#64748b','#94a3b8','#cbd5e1',
  '#fbbf24','#fcd34d','#0ea5e9','#6366f1',
];

// ── Compact data: top N + "Others" ───────────────────────────────────────────
function topN(entries, n = 8) {
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  if (sorted.length <= n) return sorted.map(([name, value]) => ({ name, value }));
  const top    = sorted.slice(0, n);
  const others = sorted.slice(n).reduce((s, [, v]) => s + v, 0);
  return [
    ...top.map(([name, value]) => ({ name, value })),
    { name: 'Others', value: others },
  ];
}

// ── Menu ──────────────────────────────────────────────────────────────────────
const MENU = [
  { key: 'professionalism', label: 'Professionalism Analysis', icon: BarChart2 },
  { key: 'recruitment',     label: 'Recruitment Analysis',     icon: Briefcase },
  { key: 'job_category',    label: 'Job Category Analysis',    icon: FileText  },
  { key: 'contract_type',   label: 'Contract Type Analysis',   icon: FileText  },
  { key: 'age',             label: 'Age Analysis',             icon: Users     },
  { key: 'personnel',       label: 'Personnel Analysis',       icon: User      },
  { key: 'attendance',      label: 'Attendance Analysis',      icon: Clock     },
];

// ── Stat badge ────────────────────────────────────────────────────────────────
function StatBadge({ label, value, color = '#f59e0b' }) {
  return (
    <div className="flex flex-col items-center justify-center bg-white border border-gray-100 rounded-xl p-3 shadow-sm min-w-[90px]">
      <span className="text-[22px] font-extrabold" style={{ color }}>{value}</span>
      <span className="text-[10px] text-gray-500 font-medium text-center leading-tight mt-0.5">{label}</span>
    </div>
  );
}

// ── Custom donut tooltip ──────────────────────────────────────────────────────
function DonutTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-[12px]">
      <p className="font-bold text-gray-800 mb-0.5">{name}</p>
      <p className="text-gray-600">{value} employees <span className="text-primary-600 font-semibold">({pct}%)</span></p>
    </div>
  );
}

// ── Active shape for donut ────────────────────────────────────────────────────
function ActiveShape(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  return (
    <g>
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#1f2937" className="text-[13px]" fontWeight="700" fontSize={13}>
        {payload.name.length > 14 ? payload.name.slice(0, 14) + '…' : payload.name}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="#f59e0b" fontWeight="700" fontSize={15}>
        {value}
      </text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill="#6b7280" fontSize={11}>
        {(percent * 100).toFixed(1)}%
      </text>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 8} outerRadius={outerRadius + 10}
        startAngle={startAngle} endAngle={endAngle} fill={fill} />
    </g>
  );
}

// ── Donut chart card ──────────────────────────────────────────────────────────
function DonutCard({ title, data, total, height = 300 }) {
  const [activeIdx, setActiveIdx] = useState(0);
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col">
      <p className="text-[13px] font-bold text-blue-700 mb-1">{title}</p>
      {total !== undefined && (
        <p className="text-[11px] text-gray-400 mb-3">{total} total · {total} active employees</p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data} cx="50%" cy="50%"
            innerRadius="52%" outerRadius="72%"
            dataKey="value" nameKey="name"
            activeIndex={activeIdx}
            activeShape={ActiveShape}
            onMouseEnter={(_, i) => setActiveIdx(i)}
          >
            {data.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
          </Pie>
          <Tooltip content={<DonutTooltip total={total || data.reduce((s,d)=>s+d.value,0)} />} />
          <Legend
            iconType="circle" iconSize={8}
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
            formatter={(value) => <span className="text-gray-700">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
      {total !== undefined && (
        <p className="text-center text-[11px] text-red-500 font-semibold mt-1">
          Total Active Employees - {total}
        </p>
      )}
    </div>
  );
}

// ── Horizontal bar card (better for long labels) ──────────────────────────────
function HBarCard({ title, data, total, height }) {
  if (!data?.length) return null;
  const h = height || Math.max(220, data.length * 28 + 60);
  const max = Math.max(...data.map(d => d.value));
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <p className="text-[13px] font-bold text-blue-700 mb-1">{title}</p>
      {total !== undefined && (
        <p className="text-[11px] text-gray-400 mb-3">Total Active Employees - {total}</p>
      )}
      <ResponsiveContainer width="100%" height={h}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10 }} domain={[0, max + 2]} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140}
            tickFormatter={v => v.length > 20 ? v.slice(0,20)+'…' : v} />
          <Tooltip
            cursor={{ fill: '#fef9ec' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-1.5 text-[12px]">
                  <p className="font-bold text-gray-800">{payload[0].payload.name}</p>
                  <p className="text-primary-600 font-semibold">{payload[0].value} employees</p>
                  {total && <p className="text-gray-400">{((payload[0].value/total)*100).toFixed(1)}%</p>}
                </div>
              );
            }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20}>
            {data.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
            <LabelList dataKey="value" position="right" style={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Vertical bar card ─────────────────────────────────────────────────────────
function VBarCard({ title, data, color = '#f59e0b', height = 260 }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <p className="text-[13px] font-bold text-blue-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
          <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-40} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-1.5 text-[12px]">
                  <p className="font-bold text-gray-800">{payload[0].payload.name}</p>
                  <p style={{ color }} className="font-semibold">{payload[0].value} employees</p>
                </div>
              );
            }}
          />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} maxBarSize={36}>
            <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Area / line chart ─────────────────────────────────────────────────────────
function AreaCard({ title, data, color = '#f59e0b', height = 260 }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <p className="text-[13px] font-bold text-blue-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow px-3 py-1.5 text-[12px]">
                  <p className="font-bold text-gray-800">{payload[0].payload.name}</p>
                  <p style={{ color }} className="font-semibold">{payload[0].value} recruited</p>
                </div>
              );
            }}
          />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2.5}
            fill="url(#areaGrad)" dot={{ fill: color, r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Employee mini card ────────────────────────────────────────────────────────
function EmpCard({ emp, maps, onClick, selected }) {
  const svcNo  = fmt(emp.p_ced_service_no);
  const name   = fmt(emp.p_report_name) || `${fmt(emp.p_first_name)} ${fmt(emp.p_last_name)}`.trim();
  const age    = calcAge(emp.p_birth_date);
  const yrs    = calcYears(emp.p_recruitment_date);
  const blood  = BLOOD_MAP[fmt(emp.p_blood_group)] || fmt(emp.p_blood_group) || '—';
  const desig  = fmt(emp.p_designation) || fmt(emp.p_info) || '';
  const imgUrl = svcNo ? `${IMAGE_BASE}?serviceNo=${encodeURIComponent(svcNo)}` : null;
  return (
    <div onClick={() => onClick?.(emp)}
      className={`border rounded-xl overflow-hidden cursor-pointer transition-all duration-150 bg-white
        ${selected ? 'border-primary-500 shadow-md ring-1 ring-primary-300' : 'border-gray-200 hover:border-primary-400 hover:shadow-sm'}`}
    >
      <div className="p-2.5 flex gap-2.5 items-start">
        <div className="w-14 h-[72px] rounded-lg overflow-hidden bg-gray-100 shrink-0 border border-gray-200 relative">
          {imgUrl && <img src={imgUrl} alt={name} className="w-full h-full object-cover absolute inset-0" onError={e => e.target.style.display='none'} />}
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-400 to-primary-600">
            <span className="text-white font-bold text-base">{name.split(' ').slice(0,2).map(p=>p[0]).join('').toUpperCase()||'?'}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono text-gray-400">{svcNo}</p>
          <div className="text-right text-[10px] leading-[18px] mt-0.5">
            {age  !== null && <div className="text-amber-600 font-semibold">Age - {age} Yrs</div>}
            {yrs  !== null && <div className="text-gray-500">Service - {yrs} Yrs</div>}
            {blood !== '—' && <div className={blood.includes('-')?'text-red-500 font-semibold':'text-gray-400'}>Blood - {blood}</div>}
          </div>
        </div>
      </div>
      <div className="px-2.5 pb-2.5 border-t border-gray-100 pt-1.5">
        <p className="text-[11px] font-bold text-gray-900 leading-snug truncate">{name}</p>
        {desig && <p className="text-[10px] text-primary-600 font-medium mt-0.5 truncate">{desig}</p>}
      </div>
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
function DetailPanel({ emp, maps }) {
  const FIELDS = ['Name','Location','Department','Division','Work Category',
    'Recruitment Date','Leaving Date','Retirement Date','NIC No','Address','Contact No','Province'];
  if (!emp) return (
    <div>
      <div className="h-8 rounded-lg mb-2 bg-gradient-to-r from-primary-400 to-primary-600" />
      {FIELDS.map(f => (
        <div key={f} className="flex items-center border-b border-gray-100 py-1.5 px-1">
          <span className="text-[11px] text-gray-400 w-28 shrink-0">{f} -</span>
          <span className="text-[11px] text-gray-300 italic">—</span>
        </div>
      ))}
    </div>
  );
  const loc  = (maps.loc?.[fmt(emp.p_location_code)])   || fmt(emp.p_location_code)   || '—';
  const dep  = (maps.dep?.[fmt(emp.p_department_code)]) || fmt(emp.p_department_code) || '—';
  const dev  = (maps.dev?.[fmt(emp.p_division_code)])   || fmt(emp.p_division_code)   || '—';
  const name = fmt(emp.p_report_name) || `${fmt(emp.p_first_name)} ${fmt(emp.p_last_name)}`.trim();
  const rows = [
    ['Name', name], ['Location', loc], ['Department', dep], ['Division', dev],
    ['Work Category', fmt(emp.p_work_category)||'—'],
    ['Recruitment Date', fmtD(emp.p_recruitment_date)],
    ['Leaving Date',     fmtD(emp.p_leaving_date)],
    ['Retirement Date',  fmtD(emp.p_retirement_date)],
    ['NIC No',    fmt(emp.p_nid)||'—'],
    ['Address',   fmt(emp.p_contact_address_1)||'—'],
    ['Contact No',fmt(emp.p_mobile)||fmt(emp.p_contact_tel)||'—'],
    ['Province',  fmt(emp.p_province)||'—'],
  ];
  return (
    <div>
      <div className="h-8 rounded-lg mb-2 bg-gradient-to-r from-primary-400 to-primary-600 flex items-center px-2">
        <span className="text-white text-[11px] font-semibold truncate">{name}</span>
      </div>
      {rows.map(([label, val]) => (
        <div key={label} className="flex items-start border-b border-gray-100 py-1 px-1">
          <span className="text-[10px] text-gray-500 w-24 shrink-0 font-medium leading-5">{label} -</span>
          <span className="text-[10px] text-gray-800 break-words flex-1 leading-5">{val}</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Analysis panels
// ═══════════════════════════════════════════════════════════════════════════════

function ProfessionalismAnalysis({ employees, maps }) {
  const [selDept, setSelDept] = useState(null);
  const [selEmp,  setSelEmp]  = useState(null);
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const divData  = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=(maps.dev?.[fmt(e.p_division_code)])||fmt(e.p_division_code)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 9);
  }, [active, maps]);

  const locData  = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=(maps.loc?.[fmt(e.p_location_code)])||fmt(e.p_location_code)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 10);
  }, [active, maps]);

  const depts = useMemo(() => {
    const d = {};
    active.forEach(e => {
      const k = fmt(e.p_department_code);
      const l = (maps.dep?.[k])||k||'Unassigned';
      if (!d[k]) d[k] = { label:l, emps:[] };
      d[k].emps.push(e);
    });
    return Object.entries(d).sort((a,b)=>a[1].label.localeCompare(b[1].label));
  }, [active, maps]);

  const stats = useMemo(() => ({
    total: active.length,
    depts: depts.length,
    divs:  new Set(active.map(e=>fmt(e.p_division_code)).filter(Boolean)).size,
    locs:  new Set(active.map(e=>fmt(e.p_location_code)).filter(Boolean)).size,
  }), [active, depts]);

  return (
    <div className="space-y-5">
      {/* Stat row */}
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Active Employees" value={stats.total} color="#f59e0b" />
        <StatBadge label="Departments"      value={stats.depts} color="#d97706" />
        <StatBadge label="Divisions"        value={stats.divs}  color="#b45309" />
        <StatBadge label="Locations"        value={stats.locs}  color="#475569" />
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard title="Professionalism - Division Wise" data={divData} total={active.length} height={320} />
        <HBarCard  title="Professionalism - Location Wise" data={locData} total={active.length} />
      </div>
      {/* Employee grid + detail */}
      <div className="flex gap-4">
        <div className="flex-1 space-y-2 overflow-y-auto max-h-[520px] pr-1">
          {depts.map(([code, { label, emps }]) => (
            <div key={code}>
              <button onClick={()=>setSelDept(selDept===code?null:code)}
                className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] font-bold uppercase tracking-wide transition-all
                  ${selDept===code ? 'bg-primary-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-primary-50 hover:text-primary-700 border border-gray-200'}`}
              >
                <span className="flex-1 text-left">{label}</span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded font-bold ${selDept===code?'bg-white/20':'bg-primary-100 text-primary-700'}`}>{emps.length}</span>
              </button>
              {selDept===code && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2 mb-1">
                  {emps.map(emp => (
                    <EmpCard key={fmt(emp.p_ced_service_no)} emp={emp} maps={maps}
                      onClick={setSelEmp}
                      selected={selEmp && fmt(selEmp.p_ced_service_no)===fmt(emp.p_ced_service_no)} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="w-56 shrink-0 bg-white border border-gray-200 rounded-xl p-2.5 shadow-sm self-start sticky top-0">
          <DetailPanel emp={selEmp} maps={maps} />
        </div>
      </div>
    </div>
  );
}

function RecruitmentAnalysis({ employees, maps }) {
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const byYear = useMemo(() => {
    const c = {};
    active.forEach(e => {
      if (!e.p_recruitment_date) return;
      const y = new Date(e.p_recruitment_date).getFullYear();
      if (!isNaN(y)) c[y] = (c[y]||0)+1;
    });
    return Object.entries(c).sort((a,b)=>a[0]-b[0]).map(([name,value])=>({name,value}));
  }, [active]);

  const byDept = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=(maps.dep?.[fmt(e.p_department_code)])||fmt(e.p_department_code)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 8);
  }, [active, maps]);

  const byLocation = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=(maps.loc?.[fmt(e.p_location_code)])||fmt(e.p_location_code)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 10);
  }, [active, maps]);

  const peak = byYear.length ? byYear.reduce((m,d)=>d.value>m.value?d:m) : null;

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Total Active"    value={active.length} color="#f59e0b" />
        <StatBadge label="Years of Data"   value={byYear.length} color="#d97706" />
        {peak && <StatBadge label={`Peak Year (${peak.name})`} value={peak.value} color="#b45309" />}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AreaCard title="Recruitment Trend by Year" data={byYear} color="#f59e0b" />
        <DonutCard title="Recruitment by Department" data={byDept} total={active.length} />
      </div>
      <HBarCard title="Recruitment by Location" data={byLocation} total={active.length} />
    </div>
  );
}

function JobCategoryAnalysis({ employees, maps }) {
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const byCat = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=(maps.empCat?.[fmt(e.p_cat_code)])||fmt(e.p_cat_code)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 9);
  }, [active, maps]);

  const byWork = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=fmt(e.p_work_category)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 9);
  }, [active]);

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Active Employees"  value={active.length}    color="#f59e0b" />
        <StatBadge label="Employee Categ."   value={byCat.length}     color="#d97706" />
        <StatBadge label="Work Categories"   value={byWork.length}    color="#475569" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard title="Job Category - Employee Category Wise" data={byCat}  total={active.length} height={320} />
        <DonutCard title="Job Category - Work Category Wise"     data={byWork} total={active.length} height={320} />
      </div>
    </div>
  );
}

function ContractTypeAnalysis({ employees }) {
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const byContract = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=fmt(e.p_contract_type||e.p_contract)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 9);
  }, [active]);

  const byEmpCat = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=fmt(e.p_employee_category)||'Unassigned'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 9);
  }, [active]);

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Active Employees" value={active.length}      color="#f59e0b" />
        <StatBadge label="Contract Types"   value={byContract.length}  color="#d97706" />
        <StatBadge label="Emp. Categories"  value={byEmpCat.length}    color="#475569" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard title="Contract Type Analysis"     data={byContract} total={active.length} height={300} />
        <DonutCard title="Employee Category Analysis" data={byEmpCat}   total={active.length} height={300} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HBarCard title="Contract Type — Breakdown" data={byContract} total={active.length} />
        <HBarCard title="Employee Category — Breakdown" data={byEmpCat} total={active.length} />
      </div>
    </div>
  );
}

function AgeAnalysis({ employees }) {
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const ageGroups = useMemo(() => {
    const g = { 'Under 25':0,'25–34':0,'35–44':0,'45–54':0,'55 & Above':0 };
    active.forEach(e => {
      const a = calcAge(e.p_birth_date);
      if (a===null) return;
      if (a<25)      g['Under 25']++;
      else if (a<35) g['25–34']++;
      else if (a<45) g['35–44']++;
      else if (a<55) g['45–54']++;
      else           g['55 & Above']++;
    });
    return Object.entries(g).map(([name,value])=>({name,value}));
  }, [active]);

  const svcGroups = useMemo(() => {
    const g = {'< 1 Year':0,'1–5 Years':0,'6–10 Years':0,'11–15 Years':0,'16–20 Years':0,'20+ Years':0};
    active.forEach(e => {
      const y = calcYears(e.p_recruitment_date);
      if (y===null) return;
      if (y<1)       g['< 1 Year']++;
      else if (y<=5) g['1–5 Years']++;
      else if (y<=10) g['6–10 Years']++;
      else if (y<=15) g['11–15 Years']++;
      else if (y<=20) g['16–20 Years']++;
      else            g['20+ Years']++;
    });
    return Object.entries(g).map(([name,value])=>({name,value}));
  }, [active]);

  const avgAge = useMemo(() => {
    const ages = active.map(e=>calcAge(e.p_birth_date)).filter(a=>a!==null);
    return ages.length ? Math.round(ages.reduce((s,a)=>s+a,0)/ages.length) : null;
  }, [active]);

  const avgSvc = useMemo(() => {
    const yrs = active.map(e=>calcYears(e.p_recruitment_date)).filter(y=>y!==null);
    return yrs.length ? (yrs.reduce((s,y)=>s+y,0)/yrs.length).toFixed(1) : null;
  }, [active]);

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Active Employees" value={active.length}      color="#f59e0b" />
        {avgAge && <StatBadge label="Avg. Age (Yrs)"   value={avgAge} color="#d97706" />}
        {avgSvc && <StatBadge label="Avg. Service (Yrs)" value={avgSvc} color="#475569" />}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard title="Age Group Distribution"     data={ageGroups}  total={active.length} height={300} />
        <DonutCard title="Service Period Distribution" data={svcGroups} total={active.length} height={300} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VBarCard title="Age Groups — Count" data={ageGroups} color="#f59e0b" />
        <VBarCard title="Service Period — Count" data={svcGroups} color="#d97706" />
      </div>
    </div>
  );
}

function PersonnelAnalysis({ employees, maps }) {
  const [selEmp, setSelEmp] = useState(null);
  const isAct = e => !e.p_inactive_type || e.p_inactive_type===''|| e.p_inactive_type==='0';
  const active = useMemo(() => employees.filter(isAct), [employees]);

  const genderData = useMemo(() => {
    const m = active.filter(e=>fmt(e.p_sex)==='M').length;
    const f = active.filter(e=>fmt(e.p_sex)==='F').length;
    return [{ name:`Male (${m})`, value:m },{ name:`Female (${f})`, value:f }];
  }, [active]);

  const bloodData = useMemo(() => {
    const c = {};
    active.forEach(e => { const k=BLOOD_MAP[fmt(e.p_blood_group)]||fmt(e.p_blood_group)||'Unknown'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 8);
  }, [active]);

  const maritalData = useMemo(() => {
    const MAP = {'1':'Married','2':'Single','3':'Divorced','4':'Widowed'};
    const c = {};
    active.forEach(e => { const k=MAP[fmt(e.p_marital_status)]||fmt(e.p_marital_status)||'Unknown'; c[k]=(c[k]||0)+1; });
    return topN(Object.entries(c), 6);
  }, [active]);

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap">
        <StatBadge label="Active Employees" value={active.length}                          color="#f59e0b" />
        <StatBadge label="Male"   value={active.filter(e=>fmt(e.p_sex)==='M').length}      color="#0ea5e9" />
        <StatBadge label="Female" value={active.filter(e=>fmt(e.p_sex)==='F').length}      color="#f43f5e" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <DonutCard title="Gender Distribution" data={genderData} total={active.length} height={260} />
        <DonutCard title="Blood Group"         data={bloodData}                         height={260} />
        <DonutCard title="Marital Status"      data={maritalData} total={active.length} height={260} />
      </div>
      <div className="flex gap-4">
        <div className="flex-1 overflow-y-auto max-h-[400px]">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 pr-1">
            {active.map(emp => (
              <EmpCard key={fmt(emp.p_ced_service_no)} emp={emp} maps={maps}
                onClick={setSelEmp}
                selected={selEmp && fmt(selEmp.p_ced_service_no)===fmt(emp.p_ced_service_no)} />
            ))}
          </div>
        </div>
        <div className="w-56 shrink-0 bg-white border border-gray-200 rounded-xl p-2.5 shadow-sm self-start sticky top-0">
          <DetailPanel emp={selEmp} maps={maps} />
        </div>
      </div>
    </div>
  );
}

// ── Attendance Analysis (real API: GetAttendanceCard) ─────────────────────────
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function classifyStatus(status) {
  const s = (status || '').toLowerCase();
  if (!s) return 'No record';
  if (s.includes('abs')) return 'Absent';
  if (s.includes('late')) return 'Late';
  if (s.includes('leave') || s.includes('lv')) return 'Leave';
  if (s.includes('holiday') || s.includes('hol')) return 'Holiday';
  if (s.includes('week') || s.includes('off')) return 'Off';
  if (s.includes('pres') || s.includes('p')) return 'Present';
  return status;
}

function AttendanceAnalysis({ employees }) {
  const dispatch = useDispatch();
  const [serviceNo, setServiceNo] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const { rows, loading, error, serviceNo: loadedFor, month: loadedMonth } = useSelector(s => s.attendance);

  React.useEffect(() => {
    if (!serviceNo) return;
    dispatch(fetchAttendanceCard({ serviceNo, month }));
  }, [dispatch, serviceNo, month]);

  const isCurrent = serviceNo && loadedFor === serviceNo && loadedMonth === month;

  const statusData = useMemo(() => {
    if (!isCurrent || !rows?.length) return [];
    const c = {};
    rows.forEach(r => {
      const statusKey = Object.keys(r).find(k => /status/i.test(k) || /day.*type/i.test(k));
      const label = classifyStatus(statusKey ? r[statusKey] : '');
      c[label] = (c[label] || 0) + 1;
    });
    return topN(Object.entries(c), 8);
  }, [rows, isCurrent]);

  const dailyHours = useMemo(() => {
    if (!isCurrent || !rows?.length) return [];
    return rows.map(r => {
      const dateKey  = Object.keys(r).find(k => /date/i.test(k));
      const hoursKey = Object.keys(r).find(k => /work.*hour/i.test(k) || /total.*hour/i.test(k) || /^hours$/i.test(k));
      const d = dateKey ? new Date(r[dateKey]) : null;
      return {
        name: d && !isNaN(d.getTime()) ? String(d.getDate()) : '—',
        value: hoursKey ? (Number(r[hoursKey]) || 0) : 0,
      };
    });
  }, [rows, isCurrent]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <EmployeePicker employees={employees} value={serviceNo} onSelect={setServiceNo} />
        {serviceNo && <MonthNavigator month={month} onChange={setMonth} />}
      </div>

      {!serviceNo ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 bg-white border border-dashed border-gray-200 rounded-2xl">
          <Clock size={36} className="opacity-30 mb-3" />
          <p className="text-sm font-semibold text-gray-500">Select an employee to see attendance analysis</p>
        </div>
      ) : loading || !isCurrent ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-9 h-9 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading attendance...</p>
        </div>
      ) : error ? (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
      ) : (
        <>
          {statusData.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DonutCard title="Attendance Status Breakdown" data={statusData} total={rows.length} height={260} />
              <VBarCard title="Hours Worked by Day" data={dailyHours} color="#0ea5e9" height={260} />
            </div>
          )}
          <AttendanceCardView rows={rows} month={month} />
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main export
// ═══════════════════════════════════════════════════════════════════════════════
export default function EmployeeAnalysis({ embedded = false }) {
  const { employees } = useSelector(s => s.employees);
  const dropdowns     = useSelector(s => s.dropdowns || {});
  const [active, setActive] = useState('professionalism');

  const maps = useMemo(() => {
    const bm = (arr, kc, kn) => { const m={}; (arr||[]).forEach(i=>{ if(i[kc]!=null) m[String(i[kc])]=String(i[kn]??i[kc]); }); return m; };
    return {
      empCat: bm(dropdowns.empCat,     'CategoryCode',   'CategoryName'),
      loc:    bm(dropdowns.locDetails, 'LocationCode',   'LocationName'),
      dep:    bm(dropdowns.depDetails, 'DepartmentCode', 'DepartmentName'),
      dev:    bm(dropdowns.devDetails, 'DivisionCode',   'DivisionName'),
    };
  }, [dropdowns]);

  const current     = MENU.find(m => m.key === active);
  const activeCount = employees.filter(e=>!e.p_inactive_type||e.p_inactive_type===''||e.p_inactive_type==='0').length;

  return (
    <div className={`flex ${embedded ? 'min-h-[700px]' : 'h-full'} bg-gray-50`}>

      {/* ── Left menu ── */}
      <div className="w-44 shrink-0 bg-white border-r border-gray-200 flex flex-col shadow-sm">
        <div className="px-3 py-3 border-b border-gray-200 bg-primary-50">
          <p className="text-primary-700 text-[10px] font-bold uppercase tracking-widest">Employee Analysis</p>
        </div>
        <nav className="flex-1 py-1 overflow-y-auto">
          {MENU.map(item => {
            const Icon  = item.icon;
            const isAct = active === item.key;
            return (
              <button key={item.key} onClick={() => setActive(item.key)}
                className={`w-full flex flex-col items-center gap-1 px-2 py-4 text-center transition-all duration-150 border-l-4
                  ${isAct ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-transparent text-gray-400 hover:bg-gray-50 hover:text-gray-700'}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors
                  ${isAct ? 'bg-primary-500 text-white shadow-sm' : 'bg-gray-100 text-gray-400'}`}>
                  <Icon size={16} />
                </div>
                <span className="text-[10px] font-semibold leading-tight">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center gap-3 sticky top-0 z-10 shadow-sm">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-100">
            {current && <current.icon size={15} className="text-primary-600" />}
          </div>
          <div>
            <h2 className="text-gray-900 font-bold text-sm">{current?.label}</h2>
            <p className="text-gray-400 text-[10px]">{employees.length} total · {activeCount} active employees</p>
          </div>
        </div>
        <div className="p-5">
          {active==='professionalism' && <ProfessionalismAnalysis employees={employees} maps={maps} />}
          {active==='recruitment'     && <RecruitmentAnalysis     employees={employees} maps={maps} />}
          {active==='job_category'    && <JobCategoryAnalysis     employees={employees} maps={maps} />}
          {active==='contract_type'   && <ContractTypeAnalysis    employees={employees} />}
          {active==='age'             && <AgeAnalysis             employees={employees} />}
          {active==='personnel'       && <PersonnelAnalysis       employees={employees} maps={maps} />}
          {active==='attendance'      && <AttendanceAnalysis      employees={employees} />}
        </div>
      </div>
    </div>
  );
}
